import { ORIENTATION } from 'cards/types';
import getZolaImageFromUuid from '@zola-helpers/client/dist/es/util/getZolaImageFromUuid';
import { ptToPx, pxToPt } from '@zola/zola-ui/src//util/font/pointPixelConversion';
import { showNewPaperImage } from 'paper/utils/experimentHelpers';

// * character_spacing value is inDesign Tracking value where 1 unit is 1/1000th of an em space
export const getLetterSpacingInPt = (fontSize, characterSpacing) =>
  fontSize * 0.001 * characterSpacing;

export const srcsetHelper = (imageUrl, width1x) =>
  `${imageUrl}?w=${width1x} 1x, ${imageUrl}?w=${width1x * 2} 2x, ${imageUrl}?w=${width1x * 3} 3x`;

export const getTextWidthInPt = (text = '', { ...fontStyles }) => {
  const {
    refFontStyle = 'normal',
    refFontWeight = 'regular',
    refFontSize = 12, // pt
    refLineHeight = 12, // pt
    refFontFamily = 'Arial',
  } = fontStyles;

  // * Create Canvas and get its context
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  // * Assign the proper font formatting
  context.font = `${refFontStyle} ${refFontWeight} ${ptToPx(refFontSize)}px/${ptToPx(
    refLineHeight
  )}px ${refFontFamily}`;

  // * Get the metrics of the specified text (in px)
  const metrics = context.measureText(text);

  return Math.ceil(pxToPt(metrics.width));
};

export const getElementOffsetPerOrientation = (
  offsetY,
  offsetX,
  pageHeight,
  pageWidth,
  isFoldable,
  pageOrientation
) => {
  let offsets = [];

  switch (pageOrientation) {
    case ORIENTATION.portrait:
      offsets = [isFoldable ? offsetX - pageWidth : offsetX, offsetY];
      break;

    case ORIENTATION.landscape:
      offsets = [offsetX, isFoldable ? offsetY - pageHeight : offsetY];
      break;

    default:
      offsets = [offsetX, offsetY];
      break;
  }

  return offsets;
};

export const getTxtCoordinatesPerOrientation = (
  width,
  height,
  txtWidth,
  txtHeight,
  orientation,
  isFolded
) => {
  let coordinates = [];
  const defaultX = Math.floor((width - txtWidth) / 2);
  const defaultY = Math.floor((height - txtHeight) / 2);

  switch (orientation) {
    case ORIENTATION.portrait:
      coordinates = [isFolded ? Math.floor(width - width / 4 - txtWidth / 2) : defaultX, defaultY];
      break;

    case ORIENTATION.landscape:
      coordinates = [
        defaultX,
        isFolded ? Math.floor(height - height / 4 - txtHeight / 2) : defaultY,
      ];
      break;

    default:
      coordinates = [defaultX, defaultY];
      break;
  }

  return coordinates;
};

export const getOffsetGuideProps = ({
  elemX,
  elemY,
  elemWidth,
  elemHeight,
  pageHeight,
  pageWidth,
  deltaLeft,
  deltaTop,
  deltaRight,
  deltaBottom,
  isFoldable,
  pageOrientation,
}) => {
  let props = {};
  const measureTopBase = elemY + pxToPt(deltaTop);
  const measureRightBase = pageWidth - (elemX + elemWidth) + pxToPt(deltaRight);
  const measureBottomBase = pageHeight - (elemY + elemHeight);
  const measureLeftBase = elemX + pxToPt(deltaLeft);

  switch (pageOrientation) {
    case ORIENTATION.portrait:
      props = {
        style: {
          topOffset: {
            top: `-${elemY}pt`,
            left: `${(ptToPx(elemWidth) + (deltaLeft - deltaRight)) / 2}px`,
            height: `${elemY + pxToPt(deltaTop)}pt`,
          },
          bottomOffset: {
            bottom: `-${pageHeight - (elemY + elemHeight)}pt`,
            left: `${(ptToPx(elemWidth) + (deltaLeft - deltaRight)) / 2}px`,
            height: `${pageHeight - (elemY + elemHeight) + pxToPt(deltaBottom)}pt`,
          },
          leftOffset: {
            top: `${(ptToPx(elemHeight) + (deltaTop - deltaBottom)) / 2}px`,
            left: isFoldable ? `-${elemX - pageWidth}pt` : `-${elemX}pt`,
            width: isFoldable
              ? `${elemX + pxToPt(deltaLeft) - pageWidth}pt`
              : `${elemX + pxToPt(deltaLeft)}pt`,
          },
          rightOffset: {
            top: `${(ptToPx(elemHeight) + (deltaTop - deltaBottom)) / 2}px`,
            right: isFoldable
              ? `-${pageWidth - (elemX + elemWidth) + pageWidth}pt`
              : `-${pageWidth - (elemX + elemWidth)}pt`,
            width: isFoldable
              ? `${pageWidth - (elemX + elemWidth) + pxToPt(deltaRight) + pageWidth}pt`
              : `${pageWidth - (elemX + elemWidth) + pxToPt(deltaRight)}pt`,
          },
        },
        measures: {
          top: Math.round(measureTopBase),
          bottom: Math.round(measureBottomBase + pxToPt(deltaBottom)),
          left: isFoldable ? Math.round(measureLeftBase - pageWidth) : Math.round(measureLeftBase),
          right: isFoldable
            ? Math.round(measureRightBase + pageWidth)
            : Math.round(measureRightBase),
        },
      };
      break;

    case ORIENTATION.landscape:
      props = {
        style: {
          topOffset: {
            top: isFoldable ? `-${elemY - pageHeight}pt` : `-${elemY}pt`,
            left: `${(ptToPx(elemWidth) + (deltaLeft - deltaRight)) / 2}px`,
            height: isFoldable
              ? `${elemY + pxToPt(deltaTop) - pageHeight}pt`
              : `${elemY + pxToPt(deltaTop)}pt`,
          },
          bottomOffset: {
            bottom: isFoldable
              ? `-${pageHeight - (elemY + elemHeight) + pageHeight}pt`
              : `-${pageHeight - (elemY + elemHeight)}pt`,
            left: `${(ptToPx(elemWidth) + (deltaLeft - deltaRight)) / 2}px`,
            height: isFoldable
              ? `${pageHeight - (elemY + elemHeight) + pxToPt(deltaBottom) + pageHeight}pt`
              : `${pageHeight - (elemY + elemHeight) + pxToPt(deltaBottom)}pt`,
          },
          leftOffset: {
            top: `${(ptToPx(elemHeight) + (deltaTop - deltaBottom)) / 2}px`,
            left: `-${elemX}pt`,
            width: `${elemX + pxToPt(deltaLeft)}pt`,
          },
          rightOffset: {
            top: `${(ptToPx(elemHeight) + (deltaTop - deltaBottom)) / 2}px`,
            right: `-${pageWidth - (elemX + elemWidth)}pt`,
            width: `${pageWidth - (elemX + elemWidth) + pxToPt(deltaRight)}pt`,
          },
        },
        measures: {
          top: isFoldable ? Math.round(measureTopBase - pageHeight) : Math.round(measureTopBase),
          bottom: isFoldable
            ? Math.round(measureBottomBase + pxToPt(deltaBottom) + pageHeight)
            : Math.round(measureBottomBase + pxToPt(deltaBottom)),
          left: Math.round(measureLeftBase),
          right: Math.round(measureRightBase),
        },
      };
      break;

    default:
      props = {
        style: {
          topOffset: {
            top: `-${elemY}pt`,
            left: `${(ptToPx(elemWidth) + (deltaLeft - deltaRight)) / 2}px`,
            height: `${elemY + pxToPt(deltaTop)}pt`,
          },
          bottomOffset: {
            bottom: `-${pageHeight - (elemY + elemHeight)}pt`,
            left: `${(ptToPx(elemWidth) + (deltaLeft - deltaRight)) / 2}px`,
            height: `${pageHeight - (elemY + elemHeight) + pxToPt(deltaBottom)}pt`,
          },
          leftOffset: {
            top: `${(ptToPx(elemHeight) + (deltaTop - deltaBottom)) / 2}px`,
            left: `-${elemX}pt`,
            width: `${elemX + pxToPt(deltaLeft)}pt`,
          },
          rightOffset: {
            top: `${(ptToPx(elemHeight) + (deltaTop - deltaBottom)) / 2}px`,
            right: `-${pageWidth - (elemX + elemWidth)}pt`,
            width: `${pageWidth - (elemX + elemWidth) + pxToPt(deltaRight)}pt`,
          },
        },
        measures: {
          top: Math.round(measureTopBase),
          bottom: Math.round(measureBottomBase + pxToPt(deltaBottom)),
          left: Math.round(measureLeftBase),
          right: Math.round(measureRightBase),
        },
      };
      break;
  }

  return props;
};

/**
 * Prioritizes shape to show based on search filters applied,
 * default_variation_shape property for a suite, then 'square'
 */
export const getFilteredShape = (searchFilters, defaultVariationShape) => {
  const filteredShape = searchFilters?.silhouettes?.[0];
  if (filteredShape) return filteredShape;
  if (defaultVariationShape) return defaultVariationShape;
  return 'square';
};

export const getActiveThumbnailUrl = (
  activeColorObject,
  searchFilters,
  paperImageTileRedesignVariation
) => {
  const filteredShape = getFilteredShape(searchFilters, activeColorObject?.default_variation_shape);

  const showNewImage = showNewPaperImage(paperImageTileRedesignVariation);
  const plpFrontImageUuidsKey = showNewImage ? 'plp_front_image_uuids_v2' : 'plp_front_image_uuids';
  const plpFrontImageUrlKey = showNewImage ? 'plp_front_image_url_v2' : 'plp_front_image_url';
  const plpFrontImageUuids =
    activeColorObject?.[plpFrontImageUuidsKey] || activeColorObject?.plp_front_image_uuids;
  const plpFrontImageUrl =
    activeColorObject?.[plpFrontImageUrlKey] || activeColorObject?.plp_front_image_url;

  return filteredShape && Object.keys(plpFrontImageUuids).length > 0
    ? getZolaImageFromUuid(plpFrontImageUuids?.[filteredShape])
    : plpFrontImageUrl;
};
